import { Switch } from "../../components/ui/switch/index.js";
import { PlatformEntitlementMark, type SuperAdminPlatformId } from "./PlatformEntitlementMark.js";

export const superAdminInputClassName =
  "w-full rounded-md border border-line/70 bg-surface px-2.5 py-2 text-[0.8125rem] text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50 disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-muted disabled:text-subtle disabled:opacity-100";

export function SuperAdminLimitField({
  id,
  label,
  description,
  unlimitedLabel,
  unlimited,
  value,
  unit,
  invalid = false,
  onUnlimitedChange,
  onValueChange,
}: {
  id: string;
  label: string;
  description: string;
  unlimitedLabel?: string;
  unlimited?: boolean;
  value: string;
  unit?: string;
  invalid?: boolean;
  onUnlimitedChange?: (unlimited: boolean) => void;
  onValueChange: (value: string) => void;
}): React.JSX.Element {
  const unlimitedId = `${id}-unlimited`;
  const showUnlimited = unlimited !== undefined && onUnlimitedChange !== undefined && unlimitedLabel !== undefined;
  return (
    <div className="grid items-center gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_9.5rem_auto]">
      <div className="grid min-w-0 gap-0.5">
        <label className="text-sm font-medium text-foreground" htmlFor={id}>
          {label}
        </label>
        <p className="m-0 text-[0.75rem] leading-relaxed text-muted">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          inputMode="decimal"
          aria-invalid={invalid}
          className={superAdminInputClassName}
          value={value}
          disabled={unlimited === true}
          onChange={(event) => onValueChange(event.target.value)}
        />
        {unit ? (
          <span className={unlimited ? "w-6 shrink-0 text-sm text-subtle" : "w-6 shrink-0 text-sm text-muted"}>
            {unit}
          </span>
        ) : (
          <span className="w-6 shrink-0" aria-hidden="true" />
        )}
      </div>
      {showUnlimited ? (
        <div className="flex items-center gap-2">
          <label className="text-sm text-foreground" htmlFor={unlimitedId}>
            {unlimitedLabel}
          </label>
          <Switch
            id={unlimitedId}
            checked={unlimited}
            data-testid={unlimitedId}
            onCheckedChange={onUnlimitedChange}
          />
        </div>
      ) : (
        <span className="hidden sm:block" />
      )}
    </div>
  );
}

export function SuperAdminPlatformToggle({
  platform,
  enabled,
  onChange,
}: {
  platform: SuperAdminPlatformId;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}): React.JSX.Element {
  const id = `super-admin-platform-${platform}`;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <label className="min-w-0 flex-1" htmlFor={id}>
        <PlatformEntitlementMark platform={platform} enabled={enabled} labeled />
      </label>
      <Switch id={id} checked={enabled} data-testid={id} onCheckedChange={onChange} />
    </li>
  );
}
